import { Box, Typography } from '@mui/joy';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from 'core/components';
import { PartBomDetails, PartBomDetailsTab, UpsertPartBom } from 'partBoms/components';
import { IPartBom, IPartBomItem } from 'partBoms/types';
import { Color, Level, Variant } from 'theme/enums';

enum MessageType {
  ADD_ITEMS = 'AddItems',
  CLOSE = 'Close',
  RESIZE = 'Resize',
}

const PART_BOM_DETAILS_WIDTH = 1440;

export const AddPartBom = () => {
  const [partBom, setPartBom] = useState<IPartBom | null>(null);

  const [payload, setPayload] = useState<{
    items: Partial<IPartBomItem>[];
    partBom: IPartBom;
  } | null>(null);

  const isSubmittingRef = useRef(false);

  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const partBomId = searchParams.get('id');

  useEffect(() => {
    if (partBomId) {
      setPartBom({ id: Number(partBomId) } as IPartBom);
      sendResizeMessage(PART_BOM_DETAILS_WIDTH);
    }
  }, [partBomId]);

  const sendCloseMessage = () => {
    window.parent.postMessage({ type: MessageType.CLOSE }, '*');
  };

  const sendResizeMessage = (width: number) => {
    window.parent.postMessage({ type: MessageType.RESIZE, payload: { width } }, '*');
  };

  const sendAddItemsMessage = () => {
    if (payload) {
      window.parent.postMessage({ type: MessageType.ADD_ITEMS, payload }, '*');
      sendCloseMessage();
    }
  };

  const handleCloseForm = () => {
    if (!isSubmittingRef.current) {
      sendCloseMessage();
    }
  };

  const handleCloseDetails = () => {
    sendCloseMessage();
    setPartBom(null);
    isSubmittingRef.current = false;
  };

  const handleSetPartBom = (partBom: IPartBom) => {
    isSubmittingRef.current = true;
    setPartBom(partBom);
    sendResizeMessage(PART_BOM_DETAILS_WIDTH);
  };

  return (
    <>
      <UpsertPartBom
        isOpen={!partBom}
        onClose={handleCloseForm}
        onSubmitSuccess={handleSetPartBom}
        fullScreen
      />

      <PartBomDetails
        isOpen={!!partBom && !payload}
        onClose={handleCloseDetails}
        partBom={partBom}
        defaultTab={PartBomDetailsTab.ITEM_LIST}
        fullScreen
        onSaveItemList={(items) => {
          setPayload({
            items: items.map((item) => ({
              id: item.id,
              quantity: item.quantity,
              uom: item.uom,
              parentPartId: item.parentPartId,
              part: item.partArr,
              notes: item.notes,
            })),
            partBom,
          });
        }}
      />

      {payload && (
        <Box sx={{ p: 2 }}>
          <Typography level={Level.TITLE_MD} sx={{ mb: 2 }}>
            {t('titlePayload')}
          </Typography>

          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.background.level1,
              p: 2,
              overflow: 'auto',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '14px',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(payload, null, 2)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              text={t('buttonLabelCompleteAndAddToMyQuote')}
              variant={Variant.SOLID}
              color={Color.PRIMARY}
              onClick={sendAddItemsMessage}
            />
            <Button
              text={t('buttonLabelClose')}
              variant={Variant.SOFT}
              color={Color.NEUTRAL}
              onClick={sendCloseMessage}
            />
          </Box>
        </Box>
      )}
    </>
  );
};
